import React, { useEffect, useState, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Coach-mark that auto-shows once when the P&L Demo dashboard first renders,
 * pointing down at the Download Forecast button. Fades out after 6 s, or on
 * user click. After dismissal, the same copy lives on the button's title
 * attribute as a standard hover tooltip (handled at the button site).
 *
 * Visibility is controlled by the parent (App.js) — it sets `show` to true
 * the first time demoViewed transitions false -> true, and never resets it.
 *
 * v5.16 fix (Jun 5, 2026): rendered via React Portal to document.body with a
 * solid opaque background. The previous in-tree absolute-positioned version
 * with a translucent purple gradient was visually swallowed by the dashboard
 * panel underneath (identical colour + nested backdrop-filter blurs).
 */
export const DemoDownloadCoachMark = ({ show, onDismiss, anchorRef }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!show) return;
    const enter = setTimeout(() => setVisible(true), 350);
    const exit = setTimeout(() => {
      setVisible(false);
      onDismiss && onDismiss();
    }, 6350);
    return () => {
      clearTimeout(enter);
      clearTimeout(exit);
    };
  }, [show, onDismiss]);

  // Recompute screen-space position whenever the bubble becomes visible OR
  // the window resizes / scrolls — keeps it pinned to the button.
  useLayoutEffect(() => {
    if (!show || !visible || !anchorRef?.current) return;
    const update = () => {
      const r = anchorRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 10, right: window.innerWidth - r.right });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [show, visible, anchorRef]);

  if (!show || !visible || !pos) return null;

  return createPortal(
    <div
      className="fixed z-[9999] w-[340px] pointer-events-auto"
      style={{ top: pos.top, right: pos.right }}
      data-testid="demo-download-coachmark"
      role="status"
      aria-live="polite"
    >
      {/* Arrow pointing UP at the Download button */}
      <div
        className="absolute right-12 -top-2 w-4 h-4 rotate-45 border-l border-t border-cyan-300/70"
        style={{ background: '#0B1530' }}
      />
      <div
        className="relative rounded-lg px-4 py-3 shadow-[0_12px_32px_rgba(0,0,0,0.65)] border border-cyan-300/70"
        style={{ background: '#0B1530' }}
      >
        <button
          onClick={() => {
            setVisible(false);
            onDismiss && onDismiss();
          }}
          className="absolute top-1.5 right-1.5 text-cyan-200/70 hover:text-white transition-colors"
          aria-label="Dismiss tip"
          data-testid="demo-download-coachmark-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <p className="text-[12.5px] leading-relaxed text-white pr-4">
          Download the output to Excel and see how our AI Agent &amp; agentic process automation create a complex, first-class Financial Forecast Report. These types of outputs and tasks can be created via our Knowledge Graph and engaging our AI Agent.
        </p>
      </div>
    </div>,
    document.body
  );
};

export default DemoDownloadCoachMark;
